const React = require("react");
// react-bootstrap ships an ESM build ("module" field) that webpack picks
// over the CJS one -- plain require() then returns the raw
// { default, __esModule } namespace instead of the component itself (unlike
// `import` statements, which babel auto-unwraps). Interop explicitly.
const interopDefault = (mod) => (mod && mod.default) ? mod.default : mod;
const Modal = interopDefault(require("react-bootstrap/Modal"));
const Tabs = interopDefault(require("react-bootstrap/Tabs"));
const Tab = interopDefault(require("react-bootstrap/Tab"));
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { Button, LinkButton, ResourceState, KeyValueTable, Kpi, HighlightedJSON, downloadJSON } = require("./ui");
const { ShippingCostCalculator } = require("./ShippingCostCalculator");
const h = React.createElement;

// require() différé (dans le composant, pas en haut du fichier) : ce module
// est requis EN RETOUR par ProductCollectionPage.js pour son propre modal de
// vue produit (cycle require A<->B). Un require() au chargement du module
// laisserait l'une des deux références à `undefined` selon l'ordre de
// chargement (CommonJS renvoie l'export partiel/incomplet du module encore
// en cours d'exécution) -- reporté ici, à l'exécution du rendu, les deux
// modules ont fini de se charger et l'export est complet des deux côtés.
const getProductCollectionPage = () => require("./ProductCollectionPage").ProductCollectionPage;

// Vue rapide sur une card CJ (Product ou Supplier) sans quitter la grille.
// kind==="product": id = DropshippingProduct._id (cache CJ, cohérent avec
// les routes existantes /dropshipping/products/:productId). Les tabs
// Sync & Compare / Console ne sont pertinents que pour un produit déjà
// importé (elakayProductId présent) et sont masqués sinon.
// kind==="supplier": id = DropshippingSupplier._id -- Info/Products/
// Statistics/JSON (Family/Admin uniquement pour ce dernier).
const CJDropshippingViewModal = ({ show, onHide, kind = "product", id, onImport, t }) => {
  const { api, isFamily } = useCJDropshipping();
  const [activeTab, setActiveTab] = React.useState("info");

  const infoResource = useDropshippingResource(
    () => {
      if (!show || !id) return Promise.resolve(null);
      return kind === "supplier" ? api.getSupplierDetail(id) : api.getProduct(id);
    },
    [api, kind, id, show]
  );
  const record = kind === "supplier" ? infoResource.data?.supplier : infoResource.data?.product;
  const elakayProductId = kind === "product" ? record?.elakayProductId : null;

  const syncResource = useDropshippingResource(
    () => (show && activeTab === "sync" && elakayProductId) ? api.compareCJProduct(elakayProductId) : Promise.resolve(null),
    [api, show, activeTab, elakayProductId]
  );

  // Triple barrière pour le tab Console : rendu conditionné à isFamily,
  // fetch qui ne se déclenche jamais si !isFamily, et la route serveur est
  // elle-même protégée par requireFamily (défense en profondeur).
  // Adressé par `id` (DropshippingProduct._id, le cache CJ) plutôt que
  // elakayProductId : le payload CJ brut existe dès le browse, avant tout
  // import -- Family doit pouvoir l'inspecter même pour un produit pas
  // encore importé. Si le produit EST importé/synced, la réponse contient
  // aussi son document Product Elakay, dans une clé séparée.
  const consoleResource = useDropshippingResource(
    () => (show && activeTab === "console" && isFamily && kind === "product" && id) ? api.getProductConsole(id) : Promise.resolve(null),
    [api, show, activeTab, isFamily, kind, id]
  );
  const consoleCJData = consoleResource.data?.dropshippingProduct ?? null;
  const consoleElakayData = consoleResource.data?.product ?? null;

  // Même triple barrière que consoleResource ci-dessus, pour le tab JSON fournisseur.
  const supplierConsoleResource = useDropshippingResource(
    () => (show && activeTab === "json" && isFamily && kind === "supplier" && id) ? api.getSupplierConsole(id) : Promise.resolve(null),
    [api, show, activeTab, isFamily, kind, id]
  );
  const supplierConsoleData = supplierConsoleResource.data?.supplier ?? null;

  const [copyMessage, setCopyMessage] = React.useState("");
  const copyConsoleJSON = (data, label) => {
    const text = JSON.stringify(data, null, 2);
    navigator.clipboard?.writeText(text).then(
      () => setCopyMessage(`${label}: ${t("h_cj_console_copied", "Copied!")}`),
      () => setCopyMessage(`${label}: ${t("h_cj_console_copy_failed", "Copy failed")}`)
    );
    setTimeout(() => setCopyMessage(""), 2000);
  };

  return h(Modal, { show, onHide, size: "xl", scrollable: true, dialogClassName: "cjds-modal", centered: true },
    h(Modal.Header, { closeButton: true },
      h(Modal.Title, null, record?.title || record?.name || t("h_cj_modal_title", "CJ Dropshipping details"))
    ),
    h(Modal.Body, null,
      h(Tabs, { activeKey: activeTab, onSelect: (key) => setActiveTab(key), id: "cjds-modal-tabs", className: "cjds-modal-tabs" },
        h(Tab, { eventKey: "info", title: t("h_cj_modal_tab_info", "Info") },
          h(ResourceState, { loading: infoResource.loading, error: infoResource.error, empty: !record, onRetry: infoResource.reload, t },
            record ? h(KeyValueTable, { data: record, t, excludeKeys: ["__v"] }) : null
          )
        ),

        kind === "product" ? h(Tab, { eventKey: "sync", title: t("h_cj_modal_tab_sync", "Sync & Compare") },
          !elakayProductId
            ? h("div", { className: "cjds-state" },
                h("h3", null, t("h_cj_not_imported_title", "Not imported yet")),
                h("p", { className: "cjds-muted" }, t("h_cj_not_imported_desc", "Import this product to Elakay to compare and synchronize its fields.")),
                // Ouvre le même CJSyncModal 3 étapes (mode="import") que les
                // boutons "Add" de ProductCard -- pas de page/route séparée
                // (voir CJSyncModal.js : "Add" ET "Sync" ouvrent désormais le
                // même wizard, aucun appel réseau avant "Finalize Sync").
                record && onImport ? h(Button, { onClick: () => onImport(record) }, t("h_cj_import", "Import to Elakay")) : null
              )
            : h(ResourceState, { loading: syncResource.loading, error: syncResource.error, empty: !syncResource.data?.comparison, onRetry: syncResource.reload, t },
                h("table", { className: "cjsync-table" },
                  h("thead", null, h("tr", null,
                    h("th", null, t("h_cjsync_col_field", "Field")),
                    h("th", null, t("h_cjsync_col_elakay", "Elakay Value")),
                    h("th", null, t("h_cjsync_col_cj", "CJ Value")),
                    h("th", null, t("h_cjsync_col_status", "Status"))
                  )),
                  h("tbody", null, ...(syncResource.data?.comparison || []).map((row) => h("tr", { key: row.field },
                    h("td", null, row.field),
                    h("td", null, JSON.stringify(row.elakayValue)),
                    h("td", null, JSON.stringify(row.cjValue)),
                    h("td", null, h("span", { className: `cjsync-badge cjsync-badge--${row.status}` }, row.status))
                  )))
                ),
                h(LinkButton, { to: `/dropshipping/products/${elakayProductId}`, variant: "secondary" },
                  t("h_cj_open_full_editor", "Open full editor for advanced mapping"))
              )
        ) : null,

        kind === "product" ? h(Tab, { eventKey: "shipping", title: t("h_cj_modal_tab_shipping", "Shipping Cost Calculation") },
          record ? h(ShippingCostCalculator, { productId: record._id, costPrice: record.costPrice, t }) : null
        ) : null,

        (kind === "product" && isFamily) ? h(Tab, { eventKey: "console", title: t("h_cj_modal_tab_console", "Console") },
          h(ResourceState, { loading: consoleResource.loading, error: consoleResource.error, empty: !consoleResource.data, onRetry: consoleResource.reload, t },
            h("div", { className: "cjds-console-group" },
              copyMessage ? h("div", { className: "cjds-console-copy-msg" }, copyMessage) : null,

              // Bloc CJ : toujours présent (le cache DropshippingProduct
              // existe dès le browse, avant tout import).
              h("div", { className: "cjds-console" },
                h("div", { className: "cjds-console-toolbar" },
                  h("h5", { className: "cjds-console-heading" }, t("h_cj_console_cj_heading", "CJ Dropshipping (raw source data)")),
                  h(Button, { variant: "secondary", onClick: () => copyConsoleJSON(consoleCJData, "CJ") }, t("h_cj_console_copy", "Copy JSON")),
                  h(Button, { variant: "secondary", onClick: () => downloadJSON(consoleCJData, `cj-product-${id}.json`) }, t("h_cj_console_download", "Download JSON"))
                ),
                h(HighlightedJSON, { data: consoleCJData })
              ),

              // Bloc Elakay : uniquement si ce produit CJ est importé/lié --
              // sinon rien à comparer, pas de bloc vide trompeur.
              consoleElakayData ? h("div", { className: "cjds-console" },
                h("div", { className: "cjds-console-toolbar" },
                  h("h5", { className: "cjds-console-heading" }, t("h_cj_console_elakay_heading", "Elakay Product (synced)")),
                  h(Button, { variant: "secondary", onClick: () => copyConsoleJSON(consoleElakayData, "Elakay") }, t("h_cj_console_copy", "Copy JSON")),
                  h(Button, { variant: "secondary", onClick: () => downloadJSON(consoleElakayData, `elakay-product-${id}.json`) }, t("h_cj_console_download", "Download JSON"))
                ),
                h(HighlightedJSON, { data: consoleElakayData })
              ) : h("p", { className: "cjds-muted" }, t("h_cj_not_imported_desc", "Import this product to Elakay to also see its synced Elakay-side data here."))
            )
          )
        ) : null,

        // Tab "Products" fournisseur : même composant/pagination/design que
        // le catalogue produits, simplement scoped à ce fournisseur (prop
        // `embedded` saute le PageHeader interne, déjà porté par Modal.Title).
        kind === "supplier" ? h(Tab, { eventKey: "products", title: t("h_cj_modal_tab_products", "Products") },
          record ? h(getProductCollectionPage(), { supplierId: id, embedded: true }) : null
        ) : null,

        // Statistics : uniquement des champs réels et déjà présents sur le
        // document supplier (pas de "Warehouses"/"Shipping Methods" -- aucune
        // donnée de ce type n'existe pour un fournisseur CJ synthétique).
        kind === "supplier" ? h(Tab, { eventKey: "stats", title: t("h_cj_modal_tab_stats", "Statistics") },
          h(ResourceState, { loading: infoResource.loading, error: infoResource.error, empty: !record, onRetry: infoResource.reload, t },
            record ? h("div", { className: "cjds-kpis" },
              h(Kpi, { value: record.totalProducts || record.productCount || 0, label: t("h_cj_stat_total_products", "Total Products") }),
              h(Kpi, { value: record.rating ? `★ ${Number(record.rating).toFixed(1)}` : t("h_cj_na", "N/A"), label: t("h_cj_stat_avg_rating", "Average Rating") }),
              h(Kpi, { value: (record.shippingCountries || []).length, label: t("h_cj_stat_countries", "Countries") }),
              h(Kpi, { value: record.lastSyncAt ? new Date(record.lastSyncAt).toLocaleString() : t("h_cj_never", "Never"), label: t("h_cj_stat_last_sync", "Last Sync") })
            ) : null
          )
        ) : null,

        // JSON brut assaini, réservé à Family (triple barrière identique au
        // tab Console produit ci-dessus, voir supplierConsoleResource).
        (kind === "supplier" && isFamily) ? h(Tab, { eventKey: "json", title: t("h_cj_modal_tab_json", "JSON") },
          h(ResourceState, { loading: supplierConsoleResource.loading, error: supplierConsoleResource.error, empty: !supplierConsoleData, onRetry: supplierConsoleResource.reload, t },
            h("div", { className: "cjds-console-group" },
              copyMessage ? h("div", { className: "cjds-console-copy-msg" }, copyMessage) : null,
              h("div", { className: "cjds-console" },
                h("div", { className: "cjds-console-toolbar" },
                  h("h5", { className: "cjds-console-heading" }, t("h_cj_console_supplier_heading", "CJ Supplier (raw source data)")),
                  h(Button, { variant: "secondary", onClick: () => copyConsoleJSON(supplierConsoleData, "Supplier") }, t("h_cj_console_copy", "Copy JSON")),
                  h(Button, { variant: "secondary", onClick: () => downloadJSON(supplierConsoleData, `cj-supplier-${id}.json`) }, t("h_cj_console_download", "Download JSON"))
                ),
                h(HighlightedJSON, { data: supplierConsoleData })
              )
            )
          )
        ) : null
      )
    )
  );
};

module.exports = { CJDropshippingViewModal };
